// Bundle entry — esbuild compiles js/core/** (real ES modules) into
// js/core.min.js, an IIFE that exposes window.Ordinex for the legacy
// classic-script pages. New code imports modules directly; legacy code
// reaches the same APIs through window.Ordinex until it is migrated.

import { CONFIG } from './config.js';
import { loadOrgContext, getOrgContext, clearOrgContext, saveOrgBranding } from './org.js';
import { orgPath, signedUrl, uploadOrgAsset } from './files.js';
import { fmtDate, fmtMoney, fmtNumber, unitLabel } from './format.js';
import { t, setLocale, extend, apply as applyI18n } from './i18n.js';
import { getPdfBrand } from '../pdf/branding.js';
import * as pdfStyle from '../pdf/style.js';
import * as pdfTokens from '../pdf/tokens.js';
import { applyBranding } from './shell.js';
import { loadFormSchemas, resolveSchema, formKeyFor, customForms } from '../forms/schemas.js';
import { activePacks, tradeForProject, hiddenNeutralForms, hiddenForms, visibleTypes } from '../forms/pack-context.js';
import * as formRegistry from '../forms/registry.js';
import * as formBuilder from '../forms/builder-model.js';
import { registerSimpleFields, checklistOptions, checklistFlagged } from '../forms/fields.js';
import { groupCatalog } from '../forms/delivery-catalog.js';
import { buildOfflineRecord, buildClockRecord, orderPunchRecords, classifyPunchError } from './offline-record.js';
import { putLocalDraft, getLocalDraft, removeLocalDraft, newerDraft } from './local-drafts.js';
import { compressImage, fitDimensions } from './image-compress.js';
import { weekKeyFor, weekBuckets, buildMaterialPulse } from './project-pulse.js';
import { pulseChartSvg, pulseLegend, fmtPulseValue } from './pulse-chart.js';
import { jobNumber, displayName } from './project-ref.js';
import * as identity from './identity.js';
import * as jobValue from './job-value.js';
import * as productionAudit from './production-audit.js';
import * as productionReport from './production-report.js';
import * as scaffoldRental from './scaffold-rental.js';
import * as costTopics from './cost-topics.js';
import * as costRecipe from './cost-recipe.js';
import * as checklist from './checklist.js';
import * as taskPlan from './task-plan.js';
import * as taskCosts from './task-costs.js';
import * as unitCost from './unit-cost.js';
import * as timesheetActivity from './timesheet-activity.js';
import * as timesheetActivityUi from './timesheet-activity-ui.js';
import * as period from './period.js';
import * as costWarnings from './cost-warnings.js';
import * as projectRollup from './project-rollup.js';
import * as jobWorkspace from './job-workspace.js';
import * as jobOverview from './job-overview.js';
import * as fieldNav from './field-nav.js';
import * as docUrls from './doc-urls.js';
import * as deliveryRow from './delivery-row.js';
import * as productionLabels from './production-labels.js';
import * as productionOutliers from './production-outliers.js';
import * as sickAllowance from './sick-allowance.js';
import * as contractLineLock from './contract-line-lock.js';
import * as syncRepair from './sync-repair.js';
import * as productionCrew from './production-crew.js';
import { ringSvg } from './ring-gauge.js';
import * as csv from './csv.js';
import * as timeOff from './time-off.js';
import { storedTheme, applyTheme, toggleTheme, isDark } from './theme.js';
import { showToast, hideToast } from './toast.js';
import * as fieldGuards from './field-guards.js';
import * as timeClock from './time-clock.js';
import * as payrollExport from './payroll-export.js';
import * as clockCorrection from './clock-correction.js';
import { plainErrorKey } from './plain-error.js';

window.Ordinex = {
  CONFIG,
  org: { load: loadOrgContext, get: getOrgContext, clear: clearOrgContext, save: saveOrgBranding },
  files: { orgPath, signedUrl, upload: uploadOrgAsset },
  fmt: { date: fmtDate, money: fmtMoney, number: fmtNumber, unit: unitLabel },
  i18n: { t, setLocale, extend, apply: applyI18n },
  pdf: { getBrand: getPdfBrand, style: pdfStyle, tokens: pdfTokens },
  shell: { apply: applyBranding },
  forms: { load: loadFormSchemas, resolve: resolveSchema, keyFor: formKeyFor, custom: customForms, registry: formRegistry, builder: formBuilder,
           checklistOptions, checklistFlagged },
  packs: { active: activePacks, tradeForProject, hidden: hiddenNeutralForms, hiddenForms, visibleTypes },
  offlineRecord: { build: buildOfflineRecord, buildClock: buildClockRecord, orderPunches: orderPunchRecords, classifyPunch: classifyPunchError },
  localDrafts: { put: putLocalDraft, get: getLocalDraft, remove: removeLocalDraft, newer: newerDraft },
  image: { compress: compressImage, fit: fitDimensions },
  pulse: { weekKeyFor, weekBuckets, buildMaterialPulse,
           chartSvg: pulseChartSvg, legend: pulseLegend, fmtValue: fmtPulseValue },
  identity,
  jobValue,
  productionAudit,
  productionReport,
  scaffoldRental,
  costTopics,
  costRecipe,
  checklist,
  taskPlan,
  taskCosts,
  unitCost,
  timesheetActivity,
  timesheetActivityUi,
  period,
  costWarnings,
  projectRollup,
  jobWorkspace,
  jobOverview,
  fieldNav,
  docUrls,
  deliveryRow,
  productionLabels,
  productionOutliers,
  sickAllowance,
  contractLineLock,
  syncRepair,
  productionCrew,
  ring: { svg: ringSvg },
  deliveryCatalog: { group: groupCatalog },
  project: { jobNumber, displayName },
  csv,
  timeOff,
  theme: { storedTheme, applyTheme, toggleTheme, isDark },
  toast: showToast,
  guards: fieldGuards,
  timeClock,
  payrollExport,
  clockCorrection,
  plainErrorKey,
};
// The toast is a bare global on both pages (40+ legacy call sites say
// `showToast(...)`); it used to be defined by js/app.js only, so the admin
// panel never showed one. core.min.js loads before app.js and admin.min.js.
window.showToast = showToast;
window.hideToast = hideToast;

// Populate the registry with the built-in simple field types (Onda 3a). The 14
// composite widgets are registered by the field app itself (they still live in
// js/app.js), so the registry is complete only once app.js has booted.
registerSimpleFields(formRegistry.register);
